import { useContext, useEffect, useRef, type CSSProperties } from "react";
import { toast } from "../ui/Toast";
import { track, trackOnce } from "../track";
import { formatDate } from "./copy";
import { TAGGED_SIZE } from "./kinds";
import { PageContext, type PageInfo } from "./page";
import { hasCode, isKeychain, type Language, type LeadKind, type PreviewDoc, type PriceOffer } from "../types";
import type { Words } from "./copy";
import { MultiBuyOffer, multiBuyWords, offVars, unbroken } from "./Quantity";
import { useBasketSaysDelivery } from "./selection";

/** The delivery lines this page says. Once it is ordered there is nothing left
 *  to decide: no Christmas line, no order-by or late line (the server leaves
 *  them out of a paid page's document too, and says the window from the day it
 *  was paid), and the window of an express payment made on this visit, until
 *  the page is read again. */
function useDeliveryLines(delivery: PreviewDoc["delivery"] | null | undefined) {
  const page = useContext(PageContext);
  const ordered = !!page?.ordered && !page.admin;
  return {
    arrives: (ordered && page?.paidArrives) || delivery?.arrives || "",
    christmas: ordered ? null : (delivery?.christmas ?? null),
    delivery: ordered ? null : delivery,
  };
}

/** "from <s>€42.93</s> €30.05", in the reader's word order, or "from €42.93"
 *  for a lead with no code, where the two would be the same number.
 *
 *  Etsy charges by size and by package, so no single number is the price: this
 *  is where it starts, the smallest size, before the code and with it. It used
 *  to be one number with nothing to say it was the smallest of three sizes,
 *  and the size the site recommends cost a fifth more at checkout. */
export function FromPrice({ price, words: t }: { price: PriceOffer; words: Words }) {
  // The sentence decides where "from" goes: before the price in most
  // languages, after it in Chinese.
  const [before = "", after = ""] = t.t("fromPrice", { price: "\u0000" }).split("\u0000");
  return (
    <>
      {before && <span className="small muted">{before}</span>}
      {price.full !== price.now && (
        <>
          <s className="muted">{price.full}</s>{" "}
        </>
      )}
      <strong className="price-now mono">{price.now}</strong>
      {after && <span className="small muted">{after}</span>}
    </>
  );
}

/** Every size with the code, so the size picker on Etsy holds no surprise.
 *  For a piece with a pet, the Etsy option it has to be ordered as. Nothing on
 *  a preview written before the grid existed, which had one number only. A
 *  lead with no code is shown the same sizes at full price, under a title that
 *  does not mention one.
 *
 *  Where the page takes the payment itself, each size is a button that opens
 *  the sheet with that size already picked: the ladder looked like a row of
 *  choices, and tapping one did nothing. A size the owner recommends
 *  (TAGGED_SIZE, none today) wears a "Recommended" tag; the size they picked
 *  last time on this device is drawn pressed. */
export function Ladder({
  price,
  code,
  kind,
  picked,
  words: t,
  onPick,
}: {
  price: PriceOffer;
  /** The lead's code, "" for none. */
  code: string;
  /** Whose sizes these are, for the tag on the recommended one. Left out,
   *  no size is tagged. */
  kind?: LeadKind | null;
  /** The size they already picked, if any: drawn pressed. */
  picked?: string | null;
  words: Words;
  /** Tapping a size picks it. Left out, the sizes are only read. */
  onPick?: (size: string) => void;
}) {
  if (!price.sizes?.length) return null;
  const withCode = hasCode(code, price);
  const popular = kind ? TAGGED_SIZE[kind] : null;
  return (
    <div style={{ marginTop: 10 }}>
      <div className="tiny muted">
        {price.package
          ? t.t(withCode ? "ladderTitlePackage" : "ladderTitlePackageFull", { package: price.package })
          : t.t(withCode ? "ladderTitle" : "ladderTitleFull")}
      </div>
      {/* One row a size, the price at the right edge: three pills with a tag
          on one of them no longer fitted a phone's width in one line. */}
      <div className="sizes" role={onPick ? "group" : undefined}>
        {price.sizes.map((s) => {
          const on = s.size === picked;
          const inner = (
            <>
              <span className="sizes-size">{s.size}</span>
              {s.size === popular && <span className="size-tag">{t.t("recommended")}</span>}
              <strong className="mono sizes-price">{s.now}</strong>
            </>
          );
          return onPick ? (
            <button
              type="button"
              className={`sizes-row${on ? " sizes-on" : ""}`}
              aria-pressed={on}
              key={s.size}
              onClick={() => onPick(s.size)}
            >
              {inner}
            </button>
          ) : (
            <div className={`sizes-row${on ? " sizes-on" : ""}`} key={s.size}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Whether a tapped size can open the sheet on it: only where the page takes
 *  the payment itself, since the Etsy steps have no size to pick, and not once
 *  it is paid for (the studio's look keeps it, as it keeps the button). */
export function sizesPickable(page: PageInfo | null): boolean {
  return !!page?.checkout && (!page.ordered || page.admin);
}

/** Whether the page shows a price card at all. Several pieces are
 *  alternatives, each with its own product, price and sizes, so both of those
 *  move up onto the cards and the card shows only what is true of all of
 *  them: the code, and the day it stops working. Nothing is added up, because
 *  picking one of three is not buying three. On a page of several with no
 *  code, everything the price card held has moved onto the piece cards, so it
 *  is left out rather than shown as an empty box. */
export function hasPriceCard(preview: PreviewDoc): boolean {
  const several = (preview.pieces?.length ?? 1) > 1;
  const price = preview.price;
  return !!price && (!several || hasCode(preview.promoCode, price));
}

/** The price, right under the drawing and the question about it.
 *
 *  The email that sent them here quoted a price; the page used to put it
 *  three cards down, after the proof strip, and a third of the people who
 *  opened the page never reached it. It is what every size costs, the code
 *  where there is one, and the size most people take. */
export function PriceCard({
  preview,
  words: t,
  onPickSize,
  datedMultiOnly,
}: {
  preview: PreviewDoc;
  words: Words;
  /** A size tapped in the ladder, for the bar's sheet to open on. */
  onPickSize?: (size: string) => void;
  /** Say the multi-buy only inside its dated window (the Christmas, Black
   *  Friday and Cyber Week offer). On a page that shows one piece of several,
   *  the standing "one more of this size" is about someone else's piece, and
   *  is said on the card that adds it (Suggestions.tsx), not under the price
   *  of a piece it does not apply to. */
  datedMultiOnly?: boolean;
}) {
  const page = useContext(PageContext);
  const saidAbove = useBasketSaysDelivery();
  const said = useDeliveryLines(preview.delivery);
  const priceRef = useRef<HTMLDivElement>(null);

  // Knowing they reached the price is what separates "opened it and went
  // quiet" from "never got far enough to find out what it costs".
  useEffect(() => {
    const node = priceRef.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "price" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(preview.promoCode);
      track("code.copied");
      toast(t.t("copied"), "good");
    } catch {
      /* Some browsers refuse without a gesture they recognise; the code is
         written on screen either way. */
    }
  }

  const several = (preview.pieces?.length ?? 1) > 1;
  const price = preview.price;
  if (!price || !hasPriceCard(preview)) return null;
  /* A lead sent with no code is quoted the full price, so there is no code to
     name, copy or date. */
  const code = hasCode(preview.promoCode, price);
  /* The code's date only while it is ahead. The code itself has no end date on
     Etsy, so past the day the price with it is still true; a "works until" a
     day that has gone by next to it was not. */
  const promoAhead =
    code && !!preview.promoEndsAt && new Date(preview.promoEndsAt).getTime() > Date.now();
  // With no code, the VAT note on its own, and on a page of several nothing.
  const vatNote = !several && price.vatNote ? price.vatNote : "";
  /* The multi-buy, said once before anyone opens the sheet: what one more of
     the smallest size costs in the same order. Only on a page of one piece
     that takes the payment itself and has not been paid for (the studio's
     look keeps it, as it keeps the sizes), and only while the server applies
     it here. No keychain price: a keychain is a preview of its own. */
  const multi =
    !several && sizesPickable(page) && (!datedMultiOnly || !!price.multiBuy?.endsOn) ? price.multiBuy : null;
  const smallest = multi ? (price.sizes?.find((s) => s.size === price.size) ?? price.sizes?.[0]) : undefined;
  const language = page?.language ?? preview.language;
  const multiSaid = multi ? multiBuyWords(multi, t, language) : null;

  return (
    // The price line under the drawing scrolls here (PriceLine.tsx).
    <div className="card" id="price" ref={priceRef}>
      <div className="row-between">
        <div>
          {!several && <FromPrice price={price} words={t} />}
          {code ? (
            <div className="tiny muted">
              {t.t("withCode")} {preview.promoCode}
              {vatNote ? ` · ${vatNote}` : ""}
            </div>
          ) : (
            vatNote && <div className="tiny muted">{vatNote}</div>
          )}
          {/* The one piece of urgency the page had was a grey line about
              deleting their photo, which is a threat about their data
              rather than a reason to buy. This is the honest version, and
              the same date every email quotes. */}
          {promoAhead && (
            <div className="tiny muted">
              {t.t("promoEnds", {
                date: formatDate(preview.promoEndsAt!, preview.language),
              })}
            </div>
          )}
        </div>
        {code && (
          // Its own width: shrunk beside long lines, "Copy code" broke into a
          // two-line lozenge. The lines beside it wrap instead.
          <button className="btn btn-quiet btn-sm" style={{ flex: "none" }} onClick={() => void copyCode()}>
            {t.t("copyCode")}
          </button>
        )}
      </div>
      {!several && (
        <Ladder
          price={price}
          code={preview.promoCode}
          kind={preview.kind}
          picked={page?.size ?? null}
          words={t}
          onPick={sizesPickable(page) ? onPickSize : undefined}
        />
      )}
      {multi && multiSaid && smallest?.extra && (
        <div style={{ marginTop: 12 }}>
          <MultiBuyOffer badge={t.t("multiBuyOff", offVars(multi.percent))}>
            <strong>{multiSaid.title}</strong>
            {/* Chinese runs on with no space. */}
            {language === "zh" || language === "zh-hans" ? "" : " "}
            {/* Inside a dated window, with what one more usually costs and
                the window's last day (multiBuyWords). */}
            {multiSaid.card(
              unbroken(smallest.size),
              unbroken(smallest.extra),
              smallest.usualExtra ? unbroken(smallest.usualExtra) : undefined
            )}
          </MultiBuyOffer>
        </div>
      )}
      {/* When it would arrive, and before Christmas the last day to order
          for it, beside the number rather than two cards down: a gift is
          bought against a date. The server's own sentences, the same as the
          mails'. Not while the order summary above is saying them. */}
      {!saidAbove && said.arrives && (
        <p className="small" style={{ margin: "12px 0 0" }}>
          {said.arrives}
        </p>
      )}
      {!saidAbove && said.christmas && (
        <p className="small" style={{ margin: "4px 0 0", fontWeight: 600 }}>
          {said.christmas}
        </p>
      )}
      {!saidAbove && <LateLine delivery={said.delivery} language={language} style={{ margin: "4px 0 0" }} />}
    </div>
  );
}

/** That express delivery is a choice in the checkout, and what it costs, to
 *  follow the line beside the payment form that says shipping is free
 *  ("Shipping is free, with all duties and import VAT included. Express
 *  delivery available at checkout (€39)."). The price is the server's, in the
 *  page's currency (delivery.expressPrice), so this never quotes a number the
 *  checkout does not charge; "" from a server that does not send it yet, and
 *  the line is then simply not there. The space before it is the reader's:
 *  Chinese runs sentences on without one. */
export function expressNote(
  words: Words,
  delivery: PreviewDoc["delivery"] | null | undefined,
  language: Language
): string {
  if (!delivery?.expressPrice) return "";
  // Priced from the address (EXPRESS_LIVE_PRICING): from this price, not at it.
  const note = words.t(delivery.expressLive ? "expressNoteLive" : "expressNote", { price: delivery.expressPrice });
  return language === "zh" || language === "zh-hans" ? note : ` ${note}`;
}

/** In the weeks from the day after the last day to order for Christmas to
 *  the 24th, where the Christmas line stands the rest of the season: that
 *  delivery before Christmas is no longer promised, or that it arrives
 *  after (the server's `late`, the website's words), in bold as the
 *  Christmas line is; and after it, while the server sends the printable
 *  card (PRINTABLE_CARD), the card every order of those weeks gets. Nothing
 *  the rest of the year. `card` false leaves the card out, where an order
 *  would not get it from the page (the Etsy steps). */
export function LateLine({
  delivery,
  language,
  card = true,
  inline = false,
  style,
}: {
  delivery: PreviewDoc["delivery"] | null | undefined;
  language: Language;
  card?: boolean;
  /** Run-in text with no paragraph of its own, for a line that stands inside
   *  a button (a paragraph may not). */
  inline?: boolean;
  style?: CSSProperties;
}) {
  if (!delivery?.late) return null;
  const withCard = card && !!delivery.card;
  const words = (
    <>
      <span style={{ fontWeight: 600 }}>{delivery.late}</span>
      {withCard && (language === "zh" || language === "zh-hans" ? "" : " ")}
      {withCard && delivery.card}
    </>
  );
  if (inline) return <span style={style}>{words}</span>;
  return (
    <p className="small" style={style}>
      {words}
    </p>
  );
}

/** The things nobody could find out without leaving: when an order placed
 *  today would arrive (and in the weeks before Christmas, the last day to
 *  order for it), and that shipping is free and tracked with all duties and
 *  import VAT included, before Etsy shows where the parcel ships from. */
export function DetailsCard({ preview, words: t }: { preview: PreviewDoc; words: Words }) {
  const ref = useRef<HTMLDivElement>(null);
  const several = (preview.pieces?.length ?? 1) > 1;
  const price = preview.price;
  /* "Saw the price" watches this card where there is no price card (a page
     of several with no code), which is as far down as the price was. */
  const standsForPrice = !!price && !hasPriceCard(preview);
  useEffect(() => {
    const node = ref.current;
    if (!standsForPrice || !node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "price" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [standsForPrice]);

  const sizes = isKeychain(preview.kind) ? t.t("sizesKeychain") : t.t("sizes");
  // The dates and the duties line, unless the order summary is saying them.
  const saidAbove = useBasketSaysDelivery();
  const said = useDeliveryLines(preview.delivery);
  const dates = !hasPriceCard(preview) && !saidAbove;
  const duties = t.t("duties");
  /* The pill that opens the duties line word for word ("Free tracked
     shipping"), said again right under it, goes while that line is here. */
  const trust = saidAbove ? t.list("trust") : t.list("trust").filter((item) => !duties.startsWith(item));

  return (
    <div className="card stack" ref={ref}>
      {/* On a page of several, each card says its own sizes: a keychain and a
          pair do not come in the same ones. With the ladder above, the sizes
          are already said. */}
      {!several && !price?.sizes?.length && (
        <p className="small" style={{ margin: 0 }}>
          {sizes}
        </p>
      )}
      {/* The dates themselves are on the price card, beside the sizes; a
          page of several with no price card says them here. */}
      {dates && said.arrives && (
        <p className="small" style={{ margin: 0 }}>
          {said.arrives}
        </p>
      )}
      {dates && said.christmas && (
        <p className="small" style={{ margin: 0, fontWeight: 600 }}>
          {said.christmas}
        </p>
      )}
      {dates && <LateLine delivery={said.delivery} language={preview.language} style={{ margin: 0 }} />}
      <p className="small muted" style={{ margin: 0 }}>
        {t.t("production")}
      </p>
      <p className="small muted" style={{ margin: 0 }}>
        {t.t("shipping")}
      </p>
      {/* Before Etsy's own "ships from" and customs notice, which is the next
          thing they will read. The same line on every page, whatever the
          currency and wherever the parcel goes: all duties and import VAT
          are included and there is nothing to pay on delivery (owner
          decision, 9 Oct 2026), never narrowed or hedged. Said once: the
          order summary has it while there is an order. */}
      {!saidAbove && (
        <p className="small" style={{ margin: 0 }}>
          {duties}
        </p>
      )}
      <div className="row row-wrap" style={{ marginTop: 2 }}>
        {trust.map((item) => (
          <span className="pill pill-quiet" key={item}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}
